import { useApi, useWorkspace, useToast } from '~/contexts';
import { useAuth, useField, useIsMounted } from '~/hooks';
import React, { useCallback, useEffect, useState } from 'react';
import AutoComplete from './AutoComplete';
import FieldControl from './FieldControl';
import Icon from './Icon';

function ProjectTypeSelect({ value, initialValue, allowNew, ...props }) {
  const auth = useAuth();
  const api = useApi();
  const toast = useToast();
  const isMounted = useIsMounted();
  const { workspace } = useWorkspace();
  const [defaultOptions, setDefaultOptions] = useState([]);

  const handleSearch = useCallback(
    async (q) => {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTypes()
        .get({ q, isActive: true, include: initialValue ? initialValue.id : undefined, size: 1000 });

      return data;
    },
    [workspace.id, initialValue, api],
  );

  const handleAddNew = async (value) => {
    try {
      const { data } = await api.www.workspaces(workspace.id).projectTypes().upsert({ name: value });

      props.onChange({ target: { name: props.name, value: data } });
    } catch ({ message }) {
      toast.error(message);
    }
  };

  const refreshDefaultOptions = useCallback(async () => {
    const data = await handleSearch('');
    if (!isMounted.current) return;
    setDefaultOptions(data);
  }, [handleSearch, isMounted]);

  useEffect(() => {
    refreshDefaultOptions();
  }, [refreshDefaultOptions]);

  return (
    <AutoComplete
      onSearch={handleSearch}
      value={value}
      displayText={value ? value.name : ''}
      defaultOptions={defaultOptions}
      renderOption={(option) => option.name}
      {...props}>
      {({ options, inputValue, handleBlur, handleLoading }) => {
        const showAddNew =
          allowNew &&
          auth.workspace.manage &&
          inputValue &&
          inputValue !== value?.name &&
          !options.some((option) => option.name === inputValue);

        return (
          <>
            {showAddNew && (
              <>
                <AutoComplete.AddNewOption
                  disabled={!inputValue}
                  onClick={async () => {
                    handleLoading(true);
                    await handleAddNew(inputValue);
                    await refreshDefaultOptions();
                    handleBlur();
                    handleLoading(false);
                  }}>
                  <Icon icon="plus" size="xs" spaceRight />
                  Add {inputValue ? <strong>&nbsp;{inputValue}</strong> : ' New'}
                </AutoComplete.AddNewOption>
                {options.length > 0 && <AutoComplete.Separator />}
              </>
            )}

            <AutoComplete.Results showNoResults={!showAddNew} />
          </>
        );
      }}
    </AutoComplete>
  );
}

function FieldProjectTypeSelect(props) {
  const [field, meta] = useField(props);
  const error = meta.touched && meta.error;

  return (
    <FieldControl error={error}>
      <ProjectTypeSelect {...field} {...props} />
    </FieldControl>
  );
}

export default ProjectTypeSelect;
export { FieldProjectTypeSelect };
